import * as THREE from 'three';
import { isPointInPolygon } from './floorPlanDesignerGeometry.js';
import { pickWallSideMaterial } from './floorPlanThreeTextures.js';
import { resolveWallSideMaterial } from './floorPlanMaterialResolver.js';

export const WALL_FACE_SAMPLE_OFFSET_M = 0.02;

/**
 * @param {unknown} point
 * @returns {[number, number] | null}
 */
function parseImagePoint(point) {
  if (!Array.isArray(point) || point.length < 2) return null;
  const x = Number(point[0]);
  const y = Number(point[1]);
  if (!Number.isFinite(x) || !Number.isFinite(y)) return null;
  return [x, y];
}

/**
 * @param {{ x: number; z: number }} world
 * @param {{ x: number; y: number }} center
 * @param {number} metersPerPixel
 * @returns {[number, number]}
 */
export function worldToImagePoint(world, center, metersPerPixel) {
  return [
    center.x + world.x / metersPerPixel,
    center.y + world.z / metersPerPixel,
  ];
}

/**
 * @param {Record<string, unknown> | null | undefined} analysisJson
 * @returns {Array<Array<[number, number]>>}
 */
export function buildRoomPolygonsForClassification(analysisJson) {
  const rooms = Array.isArray(analysisJson?.rooms) ? analysisJson.rooms : [];
  /** @type {Array<Array<[number, number]>>} */
  const polygons = [];
  for (const room of rooms) {
    if (!room || typeof room !== 'object') continue;
    const raw = Array.isArray(room.polygon) ? room.polygon : [];
    const polygon = raw.map((pt) => parseImagePoint(pt)).filter(Boolean);
    if (polygon.length >= 3) {
      polygons.push(polygon);
    }
  }
  return polygons;
}

/**
 * @param {Object} params
 * @param {{ x: number; y: number; z: number }} params.faceCenterWorld
 * @param {{ x: number; y: number; z: number }} params.faceNormalWorld
 * @param {Array<Array<[number, number]>>} params.roomPolygonsImage
 * @param {{ x: number; y: number }} params.center
 * @param {number} params.metersPerPixel
 * @returns {'inside' | 'outside'}
 */
export function classifyWallSideFace({
  faceCenterWorld,
  faceNormalWorld,
  roomPolygonsImage,
  center,
  metersPerPixel,
}) {
  if (!roomPolygonsImage.length) return 'outside';

  const sampleWorld = {
    x: faceCenterWorld.x + faceNormalWorld.x * WALL_FACE_SAMPLE_OFFSET_M,
    z: faceCenterWorld.z + faceNormalWorld.z * WALL_FACE_SAMPLE_OFFSET_M,
  };
  const sampleImage = worldToImagePoint(sampleWorld, center, metersPerPixel);

  for (const polygon of roomPolygonsImage) {
    if (isPointInPolygon(sampleImage, polygon)) {
      return 'inside';
    }
  }
  return 'outside';
}

/**
 * @param {{ x: number; z: number }} a
 * @param {{ x: number; z: number }} b
 * @returns {number}
 */
function edgeLengthXZ(a, b) {
  return Math.hypot(b.x - a.x, b.z - a.z);
}

/**
 * @param {{ x: number; z: number }} edge
 * @param {{ x: number; z: number }} axis
 * @returns {number}
 */
function edgeAxisAlignment(edge, axis) {
  const len = Math.hypot(edge.x, edge.z);
  if (len < 1e-9) return 0;
  const dot = (edge.x * axis.x + edge.z * axis.z) / len;
  return Math.abs(dot);
}

/**
 * @param {Object} params
 * @param {{ x: number; y: number; z: number }} params.bottomStart
 * @param {{ x: number; y: number; z: number }} params.bottomEnd
 * @param {{ x: number; y: number; z: number }} params.topEnd
 * @param {{ x: number; y: number; z: number }} params.topStart
 * @param {number} params.wallLengthMeters
 * @param {number} params.segmentHeightMeters
 * @param {THREE.Material} params.material
 * @returns {THREE.Mesh | null}
 */
export function createWallSideFaceQuad({
  bottomStart,
  bottomEnd,
  topEnd,
  topStart,
  wallLengthMeters,
  segmentHeightMeters,
  material,
}) {
  if (wallLengthMeters <= 1e-4 || segmentHeightMeters <= 1e-4) return null;

  const positions = new Float32Array([
    bottomStart.x, bottomStart.y, bottomStart.z,
    bottomEnd.x, bottomEnd.y, bottomEnd.z,
    topEnd.x, topEnd.y, topEnd.z,
    topStart.x, topStart.y, topStart.z,
  ]);

  const uvs = new Float32Array([
    0, 0,
    wallLengthMeters, 0,
    wallLengthMeters, segmentHeightMeters,
    0, segmentHeightMeters,
  ]);

  const ab = new THREE.Vector3(
    bottomEnd.x - bottomStart.x,
    bottomEnd.y - bottomStart.y,
    bottomEnd.z - bottomStart.z,
  );
  const ac = new THREE.Vector3(
    topStart.x - bottomStart.x,
    topStart.y - bottomStart.y,
    topStart.z - bottomStart.z,
  );
  const normal = new THREE.Vector3().crossVectors(ab, ac).normalize();

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute(
      [
        normal.x, normal.y, normal.z,
        normal.x, normal.y, normal.z,
        normal.x, normal.y, normal.z,
        normal.x, normal.y, normal.z,
      ],
      3,
    ),
  );

  return new THREE.Mesh(geometry, material);
}

/**
 * @param {Object} params
 * @param {{ x: number; z: number }} params.a
 * @param {{ x: number; z: number }} params.b
 * @param {number} params.yBottom
 * @param {number} params.yTop
 * @param {THREE.Material} params.material
 * @returns {THREE.Mesh | null}
 */
export function createSolidWallCapQuad({ a, b, yBottom, yTop, material }) {
  const height = yTop - yBottom;
  const length = edgeLengthXZ(a, b);
  if (height <= 1e-4 || length <= 1e-4) return null;

  const bottomStart = { x: a.x, y: yBottom, z: a.z };
  const bottomEnd = { x: b.x, y: yBottom, z: b.z };
  const topEnd = { x: b.x, y: yTop, z: b.z };
  const topStart = { x: a.x, y: yTop, z: a.z };

  const positions = new Float32Array([
    bottomStart.x, bottomStart.y, bottomStart.z,
    bottomEnd.x, bottomEnd.y, bottomEnd.z,
    topEnd.x, topEnd.y, topEnd.z,
    topStart.x, topStart.y, topStart.z,
  ]);

  const ab = new THREE.Vector3(
    bottomEnd.x - bottomStart.x,
    bottomEnd.y - bottomStart.y,
    bottomEnd.z - bottomStart.z,
  );
  const ac = new THREE.Vector3(
    topStart.x - bottomStart.x,
    topStart.y - bottomStart.y,
    topStart.z - bottomStart.z,
  );
  const normal = new THREE.Vector3().crossVectors(ab, ac).normalize();

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setIndex([0, 1, 2, 0, 2, 3]);
  geometry.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute(
      [
        normal.x, normal.y, normal.z,
        normal.x, normal.y, normal.z,
        normal.x, normal.y, normal.z,
        normal.x, normal.y, normal.z,
      ],
      3,
    ),
  );

  return new THREE.Mesh(geometry, material);
}

/**
 * @param {Array<{ x: number; z: number }>} basePointsXZ
 * @param {number} y
 * @param {number} areaSign
 * @param {THREE.Material} capMaterial
 * @param {string} [wallId]
 * @returns {THREE.Mesh[]}
 */
function buildHorizontalCapMeshes(basePointsXZ, y, areaSign, capMaterial, wallId) {
  /** @type {THREE.Mesh[]} */
  const meshes = [];
  const n = basePointsXZ.length;
  if (n < 3) return meshes;

  const normalY = areaSign > 0 ? 1 : -1;
  for (let i = 1; i < n - 1; i += 1) {
    const a = basePointsXZ[0];
    const b = basePointsXZ[i];
    const c = basePointsXZ[i + 1];
    const geometry = new THREE.BufferGeometry();
    const positions =
      normalY > 0
        ? [a.x, y, a.z, b.x, y, b.z, c.x, y, c.z]
        : [a.x, y, a.z, c.x, y, c.z, b.x, y, b.z];
    geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
    geometry.setAttribute(
      'normal',
      new THREE.Float32BufferAttribute([0, normalY, 0, 0, normalY, 0, 0, normalY, 0], 3),
    );
    const mesh = new THREE.Mesh(geometry, capMaterial);
    if (wallId) mesh.userData.wallId = wallId;
    meshes.push(mesh);
  }
  return meshes;
}

/**
 * @param {Object} params
 * @param {Array<{ x: number; z: number }>} params.basePointsXZ
 * @param {number} params.yBottom
 * @param {number} params.yTop
 * @param {{ x: number; z: number } | null} params.wallAxis
 * @param {import('./floorPlanThreeTextures.js').FloorPlanMaterialSet} params.materials
 * @param {Array<Array<[number, number]>>} params.roomPolygonsImage
 * @param {{ x: number; y: number }} params.center
 * @param {number} params.metersPerPixel
 * @param {THREE.Material} params.capMaterial
 * @param {string} [params.wallId]
 * @param {'normal' | 'dark'} [params.shadeVariant]
 * @param {import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig | null} [params.wallInsideMaterialConfig]
 * @param {import('./floorPlanMaterialConfig.js').FloorPlanMaterialConfig | null} [params.wallOutsideMaterialConfig]
 * @param {import('./floorPlanThreeTextures.js').BaseTextureBundle | null} [params.textureBundle]
 * @returns {THREE.Mesh[]}
 */
export function buildTexturedWallPrismMeshes({
  basePointsXZ,
  yBottom,
  yTop,
  wallAxis,
  materials,
  roomPolygonsImage,
  center,
  metersPerPixel,
  capMaterial,
  wallId,
  shadeVariant = 'normal',
  wallInsideMaterialConfig = null,
  wallOutsideMaterialConfig = null,
  textureBundle = null,
}) {
  /** @type {THREE.Mesh[]} */
  const meshes = [];
  const n = basePointsXZ.length;
  if (n < 3) return meshes;

  const height = yTop - yBottom;
  if (height <= 1e-4) return meshes;

  const axis =
    wallAxis && Math.hypot(wallAxis.x, wallAxis.z) > 1e-9
      ? {
          x: wallAxis.x / Math.hypot(wallAxis.x, wallAxis.z),
          z: wallAxis.z / Math.hypot(wallAxis.x, wallAxis.z),
        }
      : null;

  let area = 0;
  for (let i = 0; i < n; i += 1) {
    const a = basePointsXZ[i];
    const b = basePointsXZ[(i + 1) % n];
    area += a.x * b.z - b.x * a.z;
  }
  const areaSign = Math.sign(area) || 1;

  meshes.push(
    ...buildHorizontalCapMeshes(basePointsXZ, yTop, areaSign, capMaterial, wallId),
    ...buildHorizontalCapMeshes(basePointsXZ, yBottom, -areaSign, capMaterial, wallId),
  );

  for (let i = 0; i < n; i += 1) {
    const j = (i + 1) % n;
    const a = basePointsXZ[i];
    const b = basePointsXZ[j];
    const edge = { x: b.x - a.x, z: b.z - a.z };
    const edgeLen = edgeLengthXZ(a, b);
    if (edgeLen <= 1e-4) continue;

    const isLongitudinal = axis
      ? edgeAxisAlignment(edge, axis) >= 0.85
      : n === 4 && (i === 0 || i === 2);

    if (isLongitudinal) {
      const ab = { x: b.x - a.x, y: 0, z: b.z - a.z };
      const ac = { x: 0, y: 1, z: 0 };
      const normalRaw = {
        x: ab.y * ac.z - ab.z * ac.y,
        y: ab.z * ac.x - ab.x * ac.z,
        z: ab.x * ac.y - ab.y * ac.x,
      };
      const nLen = Math.hypot(normalRaw.x, normalRaw.y, normalRaw.z) || 1;
      const faceNormal = {
        x: normalRaw.x / nLen,
        y: normalRaw.y / nLen,
        z: normalRaw.z / nLen,
      };
      const classification = classifyWallSideFace({
        faceCenterWorld: {
          x: (a.x + b.x) / 2,
          y: (yBottom + yTop) / 2,
          z: (a.z + b.z) / 2,
        },
        faceNormalWorld: faceNormal,
        roomPolygonsImage,
        center,
        metersPerPixel,
      });
      const sideMaterialConfig =
        classification === 'inside' ? wallInsideMaterialConfig : wallOutsideMaterialConfig;
      const sideMaterial = sideMaterialConfig
        ? resolveWallSideMaterial(
            materials,
            textureBundle,
            sideMaterialConfig,
            classification,
            shadeVariant,
          )
        : pickWallSideMaterial(materials, classification, shadeVariant);

      const mesh = createWallSideFaceQuad({
        bottomStart: { x: a.x, y: yBottom, z: a.z },
        bottomEnd: { x: b.x, y: yBottom, z: b.z },
        topEnd: { x: b.x, y: yTop, z: b.z },
        topStart: { x: a.x, y: yTop, z: a.z },
        wallLengthMeters: edgeLen,
        segmentHeightMeters: height,
        material: sideMaterial,
      });
      if (mesh) {
        if (wallId) mesh.userData.wallId = wallId;
        meshes.push(mesh);
      }
    } else {
      const cap = createSolidWallCapQuad({
        a,
        b,
        yBottom,
        yTop,
        material: capMaterial,
      });
      if (cap) {
        if (wallId) cap.userData.wallId = wallId;
        meshes.push(cap);
      }
    }
  }

  return meshes;
}

/**
 * Build wall side faces from centerline segment (AI/import path).
 * @param {Object} params
 * @param {{ x: number; z: number }} params.startWorld
 * @param {{ x: number; z: number }} params.endWorld
 * @param {number} params.thickness
 * @param {number} params.yBottom
 * @param {number} params.yTop
 * @param {import('./floorPlanThreeTextures.js').FloorPlanMaterialSet} params.materials
 * @param {Array<Array<[number, number]>>} params.roomPolygonsImage
 * @param {{ x: number; y: number }} params.center
 * @param {number} params.metersPerPixel
 * @param {THREE.Material} params.capMaterial
 * @param {string} [params.wallId]
 * @param {'normal' | 'dark'} [params.shadeVariant]
 * @returns {THREE.Mesh[]}
 */
export function buildCenterlineWallSegmentMeshes({
  startWorld,
  endWorld,
  thickness,
  yBottom,
  yTop,
  materials,
  roomPolygonsImage,
  center,
  metersPerPixel,
  capMaterial,
  wallId,
  shadeVariant = 'normal',
}) {
  /** @type {THREE.Mesh[]} */
  const meshes = [];
  const dx = endWorld.x - startWorld.x;
  const dz = endWorld.z - startWorld.z;
  const length = Math.hypot(dx, dz);
  if (length <= 1e-4) return meshes;

  const height = yTop - yBottom;
  if (height <= 1e-4) return meshes;

  const ux = dx / length;
  const uz = dz / length;
  const px = -uz;
  const pz = ux;
  const half = thickness / 2;

  const sideAStart = { x: startWorld.x + px * half, z: startWorld.z + pz * half };
  const sideAEnd = { x: endWorld.x + px * half, z: endWorld.z + pz * half };
  const sideBStart = { x: startWorld.x - px * half, z: startWorld.z - pz * half };
  const sideBEnd = { x: endWorld.x - px * half, z: endWorld.z - pz * half };

  const sides = [
    { start: sideAStart, end: sideAEnd, normal: { x: px, y: 0, z: pz } },
    { start: sideBStart, end: sideBEnd, normal: { x: -px, y: 0, z: -pz } },
  ];

  for (const side of sides) {
    const classification = classifyWallSideFace({
      faceCenterWorld: {
        x: (side.start.x + side.end.x) / 2,
        y: (yBottom + yTop) / 2,
        z: (side.start.z + side.end.z) / 2,
      },
      faceNormalWorld: side.normal,
      roomPolygonsImage,
      center,
      metersPerPixel,
    });
    const sideMaterial = pickWallSideMaterial(materials, classification, shadeVariant);

    const mesh = createWallSideFaceQuad({
      bottomStart: { x: side.start.x, y: yBottom, z: side.start.z },
      bottomEnd: { x: side.end.x, y: yBottom, z: side.end.z },
      topEnd: { x: side.end.x, y: yTop, z: side.end.z },
      topStart: { x: side.start.x, y: yTop, z: side.start.z },
      wallLengthMeters: length,
      segmentHeightMeters: height,
      material: sideMaterial,
    });
    if (mesh) {
      if (wallId) mesh.userData.wallId = wallId;
      meshes.push(mesh);
    }
  }

  const topPositions = new Float32Array([
    sideAStart.x, yTop, sideAStart.z,
    sideAEnd.x, yTop, sideAEnd.z,
    sideBEnd.x, yTop, sideBEnd.z,
    sideBStart.x, yTop, sideBStart.z,
  ]);
  const topGeo = new THREE.BufferGeometry();
  topGeo.setAttribute('position', new THREE.BufferAttribute(topPositions, 3));
  topGeo.setIndex([0, 1, 2, 0, 2, 3]);
  topGeo.setAttribute(
    'normal',
    new THREE.Float32BufferAttribute([0, 1, 0, 0, 1, 0, 0, 1, 0, 0, 1, 0], 3),
  );
  const topMesh = new THREE.Mesh(topGeo, capMaterial);
  if (wallId) topMesh.userData.wallId = wallId;
  meshes.push(topMesh);

  return meshes;
}

/**
 * Assign floor UVs from world X/Z (shape local x/y before rotation).
 * @param {THREE.BufferGeometry} geometry
 */
export function assignFloorWorldUVs(geometry) {
  const position = geometry.getAttribute('position');
  if (!position) return;
  const uvs = new Float32Array(position.count * 2);
  for (let i = 0; i < position.count; i += 1) {
    uvs[i * 2] = position.getX(i);
    uvs[i * 2 + 1] = position.getY(i);
  }
  geometry.setAttribute('uv', new THREE.BufferAttribute(uvs, 2));
}

/**
 * Point all floor normals to world +Y (required after baking X rotation into geometry).
 * @param {THREE.BufferGeometry} geometry
 */
export function assignFloorUpNormals(geometry) {
  const position = geometry.getAttribute('position');
  if (!position) return;
  const normals = new Float32Array(position.count * 3);
  for (let i = 0; i < position.count; i += 1) {
    normals[i * 3] = 0;
    normals[i * 3 + 1] = 1;
    normals[i * 3 + 2] = 0;
  }
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
}

/**
 * UV corners for a wall side quad (for tests).
 * @param {number} wallLengthMeters
 * @param {number} segmentHeightMeters
 * @returns {Array<[number, number]>}
 */
export function wallSideFaceUVCorners(wallLengthMeters, segmentHeightMeters) {
  return [
    [0, 0],
    [wallLengthMeters, 0],
    [wallLengthMeters, segmentHeightMeters],
    [0, segmentHeightMeters],
  ];
}
